// Reusable drag-and-drop reordering (@dnd-kit) for admin lists and tables.
//
// <SortableList> renders no DOM of its own, so it can wrap table rows inside a
// <TableBody> as well as plain <div> lists. Each row gets its ref/style/handle
// props through renderItem, so the caller decides which element is sortable.
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

// Rows only ever move up/down.
const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 });

function SortableItem({ item, index, disabled, renderItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled });

  return renderItem(item, index, {
    ref: setNodeRef,
    style: {
      transform: CSS.Translate.toString(transform),
      transition,
      position: 'relative',
      zIndex: isDragging ? 10 : undefined,
      opacity: isDragging ? 0.6 : undefined,
    },
    handleProps: disabled ? {} : { ...attributes, ...listeners },
    isDragging,
  });
}

// Six-dot grip. Spread the row's handleProps onto it so only the handle
// starts a drag (buttons/inputs elsewhere in the row keep working).
export function DragHandle({ handleProps, disabled = false }) {
  return (
    <button
      type="button"
      {...handleProps}
      disabled={disabled}
      className={`inline-flex items-center justify-center rounded p-1 text-muted-foreground touch-none ${
        disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing hover:bg-muted hover:text-foreground'
      }`}
      title={disabled ? undefined : 'Drag to reorder'}
      aria-label="Drag to reorder"
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
}

/**
 * @param items       array of objects with a unique string `id`
 * @param onReorder   (reorderedItems) => void — called after a drop that changed the order;
 *                    the parent owns the state (update it optimistically, persist, revert on error)
 * @param renderItem  (item, index, { ref, style, handleProps, isDragging }) => JSX
 * @param disabled    turn dragging off (e.g. while the list is filtered or a save is in flight)
 */
export default function SortableList({ items, onReorder, renderItem, disabled = false }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      // Portal the screen-reader live region out, so no <div> lands inside a <tbody>.
      accessibility={{ container: typeof document !== 'undefined' ? document.body : undefined }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableItem key={item.id} item={item} index={index} disabled={disabled} renderItem={renderItem} />
        ))}
      </SortableContext>
    </DndContext>
  );
}
